import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { authHeader, tokenStore } from "@/lib/token";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import AuthModal from "@/components/AuthModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  User, Camera, Palette, Upload, Check, Shield, Phone, Bell, Mail,
  Smartphone, Key, Loader2, ChevronLeft, ChevronRight, AlertTriangle, Gift, Copy, Users, Lock, Languages, MessageCircle, Clock,
  Monitor, MapPin, LogOut, Headphones, BookOpen, CreditCard
} from "lucide-react";
import SettingsBilling from "@/components/SettingsBilling";
import { useLanguage } from "@/lib/i18n";

const SETTINGS_TABS = ["profile", "account", "billing", "security", "referral", "preferences", "support"];

const COLOR_PRESETS = ["#3B82F6", "#8B5CF6", "#EC4899", "#EF4444", "#F59E0B", "#10B981", "#06B6D4", "#2FD9FF"];

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

function hexToHsv(hex) {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max ? d / max : 0, v: max };
}

function hsvToHex(h, s, v) {
  const c = v * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = v - c;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const to = (u) => Math.round((u + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
}

export default function SettingsPage() {
  const { user, login, logout, refreshProfile } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  // Rubrique ouverte = ?tab= dans l'adresse. Sur téléphone, sans rubrique on affiche le
  // menu des réglages ; le bouton retour (celui de la page ou du navigateur) y ramène.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = SETTINGS_TABS.includes(tabParam) ? tabParam : "profile";
  const showMobileMenu = !SETTINGS_TABS.includes(tabParam);
  const pushedSection = useRef(false);
  const setTab = (id) => setSearchParams({ tab: id }, { replace: true });
  const openMobileSection = (id) => {
    pushedSection.current = true;
    setSearchParams({ tab: id });
    window.scrollTo(0, 0);
  };
  const backToMobileMenu = () => {
    if (pushedSection.current) {
      pushedSection.current = false;
      navigate(-1);
    } else {
      setSearchParams({}, { replace: true });
    }
    window.scrollTo(0, 0);
  };
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile fields
  const [username, setUsername] = useState("");
  const [avatarType, setAvatarType] = useState("color");
  const [avatarPhoto, setAvatarPhoto] = useState(null);
  const [avatarStyle, setAvatarStyle] = useState("flat");
  const [gradientColors, setGradientColors] = useState(["#3B82F6"]);
  const [activeStop, setActiveStop] = useState(0);
  const [gradientAngle, setGradientAngle] = useState(135);
  const [hsv, setHsv] = useState(hexToHsv("#3B82F6"));
  const [hexInput, setHexInput] = useState("#3B82F6");
  const svRef = useRef(null);
  const hueRef = useRef(null);
  const angleRef = useRef(null);
  const svDrag = useRef(false);
  const hueDrag = useRef(false);
  const angleDrag = useRef(false);

  const avatarColor = gradientColors[activeStop] || gradientColors[0];

  // Account fields
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState(false);
  const [twofa, setTwofa] = useState({ email: false, sms: false, totp: false });
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpQr, setTotpQr] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyMethod, setVerifyMethod] = useState(null);

  // Password change fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Durée avant déconnexion automatique pour inactivité (jours)
  const [sessionDuration, setSessionDuration] = useState(30);
  const [sessionDurationSaving, setSessionDurationSaving] = useState(false);

  // Appareils connectés
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  // Parrainage
  const [referral, setReferral] = useState(null);

  // Danger zone (deactivate / delete account)
  const [dangerAction, setDangerAction] = useState(null); // null | "deactivate" | "delete"
  const [dangerPassword, setDangerPassword] = useState("");
  const [deleteConfirmWord, setDeleteConfirmWord] = useState("");
  const [dangerLoading, setDangerLoading] = useState(false);

  const fileInputRef = useRef(null);
  const hdr = useCallback(() => ({ ...authHeader() }), []);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile/me`, { headers: hdr() });
      const p = res.data;
      setProfile(p);
      setUsername(p.username || "");
      setAvatarType(p.avatar_type || "color");
      const grad = (p.avatar_gradient && p.avatar_gradient.length > 0) ? p.avatar_gradient : [p.avatar_color || "#3B82F6"];
      setGradientColors(grad);
      setActiveStop(0);
      setGradientAngle(p.avatar_gradient_angle ?? 135);
      setHsv(hexToHsv(grad[0]));
      setHexInput(grad[0]);
      setAvatarPhoto(p.avatar_photo || null);
      setAvatarStyle(p.avatar_style || "flat");
      setPhone(p.phone || "");
      setWhatsapp(p.whatsapp_notifications || false);
      setTwofa({ email: p.twofa_email, sms: p.twofa_sms, totp: p.twofa_totp });
      setSessionDuration(p.session_duration_days || 30);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hdr]);

  useEffect(() => {
    if (user) fetchProfile();
  }, [user, fetchProfile]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/referral/me`, { headers: hdr() })
      .then((res) => setReferral(res.data))
      .catch(() => {});
  }, [user, hdr]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/sessions`, { headers: hdr() });
      setSessions(res.data);
    } catch { /* ignore */ }
    finally { setSessionsLoading(false); }
  }, [hdr]);

  useEffect(() => {
    if (user) fetchSessions();
  }, [user, fetchSessions]);

  const revokeSession = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      await axios.post(`${API}/auth/sessions/${sessionId}/revoke`, {}, { headers: hdr() });
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      toast.success("Appareil déconnecté");
    } catch {
      toast.error("Erreur lors de la déconnexion de cet appareil");
    } finally {
      setRevokingId(null);
    }
  };

  const formatLastSeen = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("discord_linked") === "1") {
      toast.success("Compte Discord connecté !");
      setSearchParams({ tab: "account" }, { replace: true });
    } else if (params.get("discord_link_error")) {
      const err = params.get("discord_link_error");
      toast.error(err === "already_used" ? "Ce compte Discord est déjà lié à un autre compte VantaPulse" : "Impossible de connecter ce compte Discord");
      setSearchParams({ tab: "account" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectDiscord = () => {
    const token = tokenStore.get();
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${API}/auth/discord/link/start?origin=${origin}&token=${token}`;
  };

  const copyReferralLink = () => {
    if (!referral) return;
    navigator.clipboard.writeText(referral.referral_link)
      .then(() => toast.success("Lien copié !"))
      .catch(() => toast.error("Impossible de copier le lien"));
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/update`, {
        username,
        avatar_type: avatarType,
        avatar_color: gradientColors[0],
        avatar_gradient: gradientColors,
        avatar_gradient_angle: gradientAngle,
        avatar_photo: avatarPhoto,
        avatar_style: avatarStyle,
      }, { headers: hdr() });
      toast.success("Profil mis à jour !");
      refreshProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const saveAccount = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/account`, { phone, whatsapp_notifications: whatsapp }, { headers: hdr() });
      toast.success("Paramètres enregistrés !");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const saveSessionDuration = async (days) => {
    setSessionDuration(days);
    setSessionDurationSaving(true);
    try {
      await axios.put(`${API}/profile/account`, { session_duration_days: days }, { headers: hdr() });
      toast.success("Durée de connexion mise à jour");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSessionDurationSaving(false);
    }
  };

  const setup2FA = async (method) => {
    try {
      const res = await axios.post(`${API}/profile/2fa/setup`, { method }, { headers: hdr() });
      if (method === "totp") {
        setTotpSecret(res.data.secret);
        // QR généré par le serveur (la clé ne part vers aucun site extérieur)
        setTotpQr(res.data.qr_image || null);
      }
      setVerifyMethod(method);
      setVerifyCode("");
      toast.success(res.data.message || "Configuration lancée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const verify2FA = async () => {
    if (!verifyCode.trim()) return;
    try {
      await axios.post(`${API}/profile/2fa/verify`, { method: verifyMethod, code: verifyCode }, { headers: hdr() });
      toast.success("A2F activée !");
      setTwofa(prev => ({ ...prev, [verifyMethod]: true }));
      setVerifyMethod(null);
      setVerifyCode("");
      setTotpSecret(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code invalide");
    }
  };

  const disable2FA = async (method) => {
    try {
      await axios.post(`${API}/profile/2fa/disable`, { method }, { headers: hdr() });
      toast.success("A2F désactivée");
      setTwofa(prev => ({ ...prev, [method]: false }));
    } catch { toast.error("Erreur"); }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Tous les champs sont requis");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(`${API}/profile/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: hdr() });
      toast.success("Mot de passe modifié avec succès !");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la modification du mot de passe");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!dangerPassword) { toast.error("Entrez votre mot de passe"); return; }
    setDangerLoading(true);
    try {
      await axios.post(`${API}/profile/deactivate`, { password: dangerPassword }, { headers: hdr() });
      toast.success("Compte désactivé. Reconnectez-vous pour le réactiver.");
      setDangerAction(null);
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setDangerLoading(false); }
  };

  const handleDeleteAccount = async () => {
    if (!dangerPassword) { toast.error("Entrez votre mot de passe"); return; }
    if (deleteConfirmWord !== "SUPPRIMER") { toast.error('Tapez "SUPPRIMER" pour confirmer'); return; }
    setDangerLoading(true);
    try {
      await axios.post(`${API}/profile/delete-account`, { password: dangerPassword }, { headers: hdr() });
      toast.success("Compte supprimé définitivement");
      setDangerAction(null);
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setDangerLoading(false); }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image trop lourde (max 2 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => { setAvatarPhoto(reader.result); setAvatarType("photo"); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const applyColor = (hex, index = activeStop) => {
    setGradientColors(prev => prev.map((c, i) => i === index ? hex : c));
    setHexInput(hex);
  };

  const applyHsv = (next) => {
    setHsv(next);
    applyColor(hsvToHex(next.h, next.s, next.v));
  };

  const selectColor = (hex) => {
    setHsv(hexToHsv(hex));
    applyColor(hex);
  };

  const selectStop = (i) => {
    setActiveStop(i);
    setHsv(hexToHsv(gradientColors[i]));
    setHexInput(gradientColors[i]);
  };

  const addStop = () => {
    if (gradientColors.length >= 4) return;
    setGradientColors(prev => [...prev, avatarColor]);
    setActiveStop(gradientColors.length);
  };

  const removeStop = (i) => {
    if (gradientColors.length <= 1) return;
    const next = gradientColors.filter((_, idx) => idx !== i);
    const newActive = clamp(activeStop > i ? activeStop - 1 : activeStop, 0, next.length - 1);
    setGradientColors(next);
    setActiveStop(newActive);
    setHsv(hexToHsv(next[newActive]));
    setHexInput(next[newActive]);
  };

  const handleSvPointer = (e) => {
    const rect = svRef.current.getBoundingClientRect();
    const s = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    const v = 1 - clamp((e.clientY - rect.top) / rect.height, 0, 1);
    applyHsv({ ...hsv, s, v });
  };

  const handleHuePointer = (e) => {
    const rect = hueRef.current.getBoundingClientRect();
    const h = clamp((e.clientX - rect.left) / rect.width, 0, 1) * 360;
    applyHsv({ ...hsv, h });
  };

  const handleAnglePointer = (e) => {
    const rect = angleRef.current.getBoundingClientRect();
    setGradientAngle(Math.round(clamp((e.clientX - rect.left) / rect.width, 0, 1) * 360));
  };

  const getAvatarGradient = () => {
    if (gradientColors.length >= 2) return `linear-gradient(${gradientAngle}deg, ${gradientColors.join(", ")})`;
    const c = gradientColors[0];
    if (avatarStyle === "matte") return `linear-gradient(135deg, ${c}, ${c}88)`;
    if (avatarStyle === "metallic") return `linear-gradient(135deg, ${c}cc, white, ${c})`;
    return c;
  };

  const settingsTabs = [
    { id: "profile", label: t('settings_tab_profile'), desc: t('settings_desc_profile'), icon: User, tint: "bg-sky-500/15 text-sky-400" },
    { id: "account", label: t('settings_tab_account'), desc: t('settings_desc_account'), icon: Shield, tint: "bg-blue-500/15 text-blue-400" },
    { id: "billing", label: t('settings_tab_billing'), desc: t('settings_desc_billing'), icon: CreditCard, tint: "bg-amber-500/15 text-amber-400" },
    { id: "security", label: t('settings_tab_security'), desc: t('settings_desc_security'), icon: Lock, tint: "bg-emerald-500/15 text-emerald-400" },
    { id: "referral", label: t('settings_tab_referral'), desc: t('settings_desc_referral'), icon: Gift, tint: "bg-pink-500/15 text-pink-400" },
    { id: "preferences", label: t('settings_tab_preferences'), desc: t('settings_desc_preferences'), icon: Languages, tint: "bg-violet-500/15 text-violet-400" },
    { id: "support", label: t('settings_tab_support'), desc: t('settings_desc_support'), icon: Headphones, tint: "bg-cyan-500/15 text-cyan-400" },
  ];
  const currentTab = settingsTabs.find((x) => x.id === tab);
  const twofaOn = Boolean(twofa.email || twofa.sms || twofa.totp);

  if (!user) {
    return (
      <div className="min-h-screen bg-vanta-base flex flex-col">
        <Navbar onAuthClick={() => setShowAuth(true)} />
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode={authMode} setMode={setAuthMode} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-heading font-bold text-white/90 mb-4">Paramètres</h1>
            <p className="text-white/40 mb-6">Connectez-vous pour accéder aux paramètres</p>
            <Button onClick={() => setShowAuth(true)} className="bg-blue-600 hover:bg-blue-500">Se connecter</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-vanta-base" data-testid="settings-page">
      <Navbar onAuthClick={() => setShowAuth(true)} />
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode={authMode} setMode={setAuthMode} />

      <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-80 overflow-hidden md:block">
        <div className="absolute left-1/4 top-[-160px] h-[340px] w-[720px] rounded-full bg-gradient-to-r from-blue-600/15 via-violet-600/10 to-transparent blur-[90px]" />
      </div>
      <div className="relative max-w-3xl md:max-w-6xl mx-auto px-4 md:px-6 pt-20 md:pt-28 pb-12">
        {/* Téléphone : rubrique ouverte, avec retour au menu */}
        {!showMobileMenu && (
          <div className="md:hidden mb-5">
            <button onClick={backToMobileMenu} data-testid="settings-mobile-back" className="flex items-center -ml-1.5 py-1 text-sm text-blue-400 active:opacity-60">
              <ChevronLeft className="w-5 h-5" />{t('settings_title')}
            </button>
            <h1 className="text-2xl font-heading font-bold mt-1">{currentTab.label}</h1>
          </div>
        )}
        <h1 className={`text-2xl md:text-3xl font-heading font-bold tracking-tight mb-5 md:mb-1 ${showMobileMenu ? "" : "hidden md:block"}`}>{t('settings_title')}</h1>
        <p className="hidden md:block text-sm text-white/40 mb-8">Gère ton profil, ta sécurité, tes paiements et tes préférences.</p>

        {/* Téléphone : menu des réglages */}
        {showMobileMenu && (
          <div className="md:hidden space-y-4" data-testid="settings-mobile-menu">
            <button
              onClick={() => openMobileSection("profile")}
              data-testid="settings-mobile-profile"
              className="w-full flex items-center gap-4 p-4 bg-[#0f1019] border border-white/[0.07] rounded-2xl text-left active:bg-white/[0.04] transition-colors"
            >
              <div
                className="w-14 h-14 rounded-full border border-white/10 flex items-center justify-center text-xl font-bold overflow-hidden shrink-0"
                style={{ background: avatarType === "photo" && avatarPhoto ? `url(${avatarPhoto}) center/cover` : getAvatarGradient() }}
              >
                {!(avatarType === "photo" && avatarPhoto) && <span className="text-white/90">{(username || user.username || "?")[0].toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{username || user.username}</p>
                <p className="text-xs text-white/40 truncate">{profile?.email || user.email}</p>
                <p className="text-xs text-blue-400 mt-1">{t('settings_edit_profile')}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/25 shrink-0" />
            </button>

            <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl overflow-hidden divide-y divide-white/5">
              {settingsTabs.filter((x) => x.id !== "profile").map((x) => (
                <button
                  key={x.id}
                  onClick={() => openMobileSection(x.id)}
                  data-testid={`settings-mobile-${x.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-white/[0.04] transition-colors"
                >
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${x.tint}`}>
                    <x.icon className="w-[18px] h-[18px]" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium">{x.label}</span>
                    <span className="block text-xs text-white/35 truncate">{x.desc}</span>
                  </span>
                  {x.id === "security" && !loading && (
                    <span data-testid="settings-mobile-2fa-status" className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${twofaOn ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`}>
                      {twofaOn ? t('settings_2fa_on') : t('settings_2fa_off')}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-white/25 shrink-0" />
                </button>
              ))}
            </div>

            <button
              onClick={() => { logout(); navigate("/"); }}
              data-testid="settings-mobile-logout"
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#0f1019] border border-white/[0.07] rounded-2xl text-sm text-red-400 active:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-4 h-4" />{t('nav_logout')}
            </button>
          </div>
        )}

        <div className="md:grid md:grid-cols-[260px_minmax(0,1fr)] md:items-start md:gap-8">
        {/* Ordinateur / tablette : menu latéral */}
        <aside className="hidden md:block sticky top-24" data-testid="settings-nav">
          <div className="rounded-2xl border border-white/[0.06] bg-[#0f1019]/80 p-2 backdrop-blur">
            <div className="flex items-center gap-3 rounded-xl px-2.5 py-3">
              <div
                className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-base font-bold overflow-hidden shrink-0"
                style={{ background: avatarType === "photo" && avatarPhoto ? `url(${avatarPhoto}) center/cover` : getAvatarGradient() }}
              >
                {!(avatarType === "photo" && avatarPhoto) && <span className="text-white/90">{(username || user.username || "?")[0].toUpperCase()}</span>}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{username || user.username}</p>
                <p className="truncate text-xs text-white/40">{profile?.email || user.email}</p>
              </div>
            </div>
            <div className="mx-2 mb-1.5 h-px bg-white/[0.06]" />
            <nav className="space-y-0.5">
              {settingsTabs.map(navTab => {
                const active = tab === navTab.id;
                return (
                  <button
                    key={navTab.id}
                    onClick={() => setTab(navTab.id)}
                    data-testid={`settings-tab-${navTab.id}`}
                    className={`group relative w-full flex items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors ${active ? "bg-white/[0.07]" : "hover:bg-white/[0.035]"}`}
                  >
                    {active && <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-gradient-to-b from-blue-400 to-violet-500" />}
                    <span className={`w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 ${navTab.tint}`}>
                      <navTab.icon className="w-4 h-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[13.5px] font-medium ${active ? "text-white" : "text-white/70 group-hover:text-white"}`}>{navTab.label}</span>
                      <span className="block truncate text-[11px] text-white/30">{navTab.desc}</span>
                    </span>
                    {navTab.id === "security" && !loading && (
                      <span className={`h-2 w-2 shrink-0 rounded-full ${twofaOn ? "bg-emerald-400" : "bg-amber-400"}`} title={twofaOn ? t('settings_2fa_on') : t('settings_2fa_off')} />
                    )}
                  </button>
                );
              })}
            </nav>
            <div className="mx-2 my-1.5 h-px bg-white/[0.06]" />
            <button
              onClick={() => { logout(); navigate("/"); }}
              data-testid="settings-logout"
              className="w-full flex items-center gap-3 rounded-xl px-2.5 py-2 text-left text-[13.5px] text-red-300/80 transition-colors hover:bg-red-500/[0.08] hover:text-red-300"
            >
              <span className="w-8 h-8 rounded-[9px] flex items-center justify-center bg-red-500/10"><LogOut className="w-4 h-4" /></span>
              {t('nav_logout')}
            </button>
          </div>
        </aside>

        <div className="min-w-0">
        {currentTab && (
          <div key={tab} className="settings-head hidden md:flex items-center gap-3.5 mb-6">
            <span className={`w-11 h-11 rounded-xl flex items-center justify-center ring-1 ring-inset ring-white/[0.06] ${currentTab.tint}`}>
              <currentTab.icon className="w-5 h-5" />
            </span>
            <div>
              <h2 className="font-heading text-xl font-bold leading-tight">{currentTab.label}</h2>
              <p className="text-sm text-white/40">{currentTab.desc}</p>
            </div>
          </div>
        )}
        <div key={`content-${tab}`} className={`settings-pane ${showMobileMenu ? "hidden md:block" : ""}`} data-testid="settings-content">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
          ) : tab === "profile" ? (
            <div className="space-y-8">
              {/* Avatar */}
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-8" data-testid="avatar-section">
                <div className="flex flex-col items-center mb-6">
                  <div
                    className="w-28 h-28 rounded-full border-2 border-white/10 flex items-center justify-center text-3xl font-bold cursor-pointer hover:opacity-80 transition-opacity overflow-hidden"
                    style={{ background: avatarType === "photo" && avatarPhoto ? `url(${avatarPhoto}) center/cover` : getAvatarGradient() }}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="avatar-preview"
                  >
                    {avatarType !== "photo" && <span className="text-white/90">{(username || "?")[0].toUpperCase()}</span>}
                  </div>
                  <p className="text-white/30 text-xs mt-3">Appuyez sur l'avatar pour choisir une photo</p>
                </div>

                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />

                {/* Color / Photo toggle */}
                <div className="flex gap-1 mb-6 bg-black/30 rounded-xl p-1">
                  <button onClick={() => setAvatarType("color")} className={`flex-1 py-2 text-sm rounded-lg transition-colors ${avatarType === "color" ? 'bg-white/10 text-white' : 'text-white/40'}`}>
                    <Palette className="w-4 h-4 inline mr-1.5" />Couleur
                  </button>
                  <button onClick={() => setAvatarType("photo")} className={`flex-1 py-2 text-sm rounded-lg transition-colors ${avatarType === "photo" ? 'bg-white/10 text-white' : 'text-white/40'}`}>
                    <Camera className="w-4 h-4 inline mr-1.5" />Photo
                  </button>
                </div>

                {avatarType === "color" ? (
                  <div className="space-y-4">
                    {/* Saturation/Value square */}
                    <div
                      ref={svRef}
                      className="relative h-48 rounded-xl overflow-hidden cursor-crosshair touch-none select-none"
                      style={{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), hsl(${hsv.h}, 100%, 50%)` }}
                      onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); svDrag.current = true; handleSvPointer(e); }}
                      onPointerMove={(e) => { if (svDrag.current) handleSvPointer(e); }}
                      onPointerUp={() => { svDrag.current = false; }}
                      data-testid="sv-picker"
                    >
                      <div
                        className="absolute w-5 h-5 border-2 border-white rounded-full -translate-x-1/2 -translate-y-1/2 shadow-lg pointer-events-none"
                        style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: avatarColor }}
                      />
                    </div>

                    {/* Hue bar */}
                    <div
                      ref={hueRef}
                      className="relative h-4 rounded-full cursor-pointer touch-none select-none"
                      style={{ background: "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)" }}
                      onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); hueDrag.current = true; handleHuePointer(e); }}
                      onPointerMove={(e) => { if (hueDrag.current) handleHuePointer(e); }}
                      onPointerUp={() => { hueDrag.current = false; }}
                      data-testid="hue-slider"
                    >
                      <div
                        className="absolute top-1/2 w-5 h-5 border-2 border-white rounded-full -translate-x-1/2 -translate-y-1/2 shadow-lg pointer-events-none"
                        style={{ left: `${(hsv.h / 360) * 100}%`, background: `hsl(${hsv.h}, 100%, 50%)` }}
                      />
                    </div>

                    {/* Hex display */}
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full shrink-0 border border-white/10" style={{ background: avatarColor }} />
                      <Input
                        value={hexInput}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          if (/^#[0-9A-F]{0,6}$/.test(v)) {
                            setHexInput(v);
                            if (v.length === 7) selectColor(v);
                          }
                        }}
                        className="bg-black/30 border-white/10 font-mono text-sm"
                        data-testid="hex-input"
                      />
                    </div>

                    {/* Presets */}
                    <div className="grid grid-cols-8 gap-2 sm:flex sm:flex-wrap">
                      {COLOR_PRESETS.map((c) => (
                        <button key={c} onClick={() => selectColor(c)} aria-label={c} className={`w-full aspect-square sm:w-8 sm:h-8 rounded-full border-2 transition-transform hover:scale-110 ${avatarColor === c ? 'border-white scale-110' : 'border-white/10'}`} style={{ background: c }} />
                      ))}
                    </div>

                    {/* Gradient stops */}
                    <div>
                      <Label className="text-xs text-white/40 uppercase tracking-wider mb-2 block">Dégradé — 4 couleurs max</Label>
                      <div className="flex items-center gap-3">
                        {gradientColors.map((c, i) => (
                          <div key={i} className="relative">
                            <button
                              onClick={() => selectStop(i)}
                              className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${activeStop === i ? 'border-white scale-110' : 'border-white/15'}`}
                              style={{ background: c }}
                              data-testid={`gradient-stop-${i}`}
                            />
                            {gradientColors.length > 1 && (
                              <button
                                onClick={() => removeStop(i)}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-[#0a0a12] border border-white/30 rounded-full text-white/70 text-[10px] leading-none flex items-center justify-center hover:bg-red-500 hover:border-red-500 hover:text-white transition-colors"
                                data-testid={`remove-stop-${i}`}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        {gradientColors.length < 4 && (
                          <button onClick={addStop} className="w-9 h-9 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center text-white/30 hover:border-white/40 text-lg" data-testid="add-gradient-stop">+</button>
                        )}
                      </div>
                    </div>

                    {/* Gradient angle */}
                    {gradientColors.length >= 2 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs text-white/40 uppercase tracking-wider">Angle du dégradé</Label>
                          <span className="text-xs font-mono text-cyan-400">{gradientAngle}°</span>
                        </div>
                        <div
                          ref={angleRef}
                          className="relative h-4 rounded-full cursor-pointer touch-none select-none bg-white/10"
                          onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); angleDrag.current = true; handleAnglePointer(e); }}
                          onPointerMove={(e) => { if (angleDrag.current) handleAnglePointer(e); }}
                          onPointerUp={() => { angleDrag.current = false; }}
                          data-testid="angle-slider"
                        >
                          <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" style={{ width: `${(gradientAngle / 360) * 100}%` }} />
                          <div
                            className="absolute top-1/2 w-5 h-5 bg-white rounded-full -translate-x-1/2 -translate-y-1/2 shadow-lg pointer-events-none"
                            style={{ left: `${(gradientAngle / 360) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Style: Flat / Matte / Metallic */}
                    <div className="flex gap-2">
                      {["flat", "matte", "metallic"].map(s => (
                        <button key={s} onClick={() => setAvatarStyle(s)} className={`flex-1 py-2 text-sm rounded-lg transition-colors ${avatarStyle === s ? 'bg-white/10 text-white border border-white/20' : 'bg-black/20 text-white/40 border border-white/5'}`}
                          data-testid={`style-${s}`}
                        >
                          {s === "flat" ? "Uni" : s === "matte" ? "Mat" : "Métallisé"}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-white/20 transition-colors"
                    >
                      <Upload className="w-8 h-8 mx-auto text-white/30 mb-2" />
                      <p className="text-white/40 text-sm">Choisir une image</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Username */}
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <Label className="text-xs text-white/40 uppercase tracking-wider mb-2 block">Pseudo</Label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Votre pseudo unique"
                  className="bg-black/30 border-white/10 mb-2"
                  data-testid="username-input"
                />
                <p className="text-white/20 text-xs">Unique — ce pseudo ne peut pas être partagé avec quelqu'un d'autre.</p>
              </div>

              <Button onClick={saveProfile} disabled={saving} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-xl py-3" data-testid="save-profile-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Sauvegarder
              </Button>
            </div>
          ) : tab === "account" ? (
            /* Account tab */
            <div className="space-y-6">
              {/* Phone */}
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <Label className="text-xs text-white/40 uppercase tracking-wider mb-2 block">Numéro de téléphone</Label>
                <div className="flex gap-2">
                  <Phone className="w-5 h-5 text-white/30 mt-2.5 shrink-0" />
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+33 6 12 34 56 78"
                    className="bg-black/30 border-white/10"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {/* WhatsApp notifications */}
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Bell className="w-5 h-5 text-green-400 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Notifications WhatsApp</p>
                      <p className="text-xs text-white/30">Recevoir les annonces par message</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setWhatsapp(!whatsapp)}
                    data-testid="whatsapp-toggle"
                    className={`w-12 h-6 rounded-full transition-colors relative shrink-0 ${whatsapp ? 'bg-green-500' : 'bg-white/10'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${whatsapp ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              {/* Comptes connectés */}
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-[#5865F2]" />Discord</h2>
                <p className="text-xs text-white/40 mb-4">Une fois connecté, vous pourrez recevoir un code de connexion par message privé Discord, en plus du mot de passe.</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {user?.discord_linked ? (
                      <>
                        <Check className="w-5 h-5 text-green-400" />
                        <p className="text-sm font-medium text-white/80">Compte Discord connecté</p>
                      </>
                    ) : (
                      <p className="text-sm text-white/40">Aucun compte Discord connecté</p>
                    )}
                  </div>
                  {!user?.discord_linked && (
                    <Button
                      onClick={connectDiscord}
                      data-testid="connect-discord-btn"
                      className="bg-[#5865F2] hover:bg-[#4752C4] rounded-xl text-sm"
                    >
                      Connecter Discord
                    </Button>
                  )}
                </div>
              </div>

              {/* Change Password */}
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Key className="w-5 h-5 text-blue-400" />Changer le mot de passe</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-white/40 uppercase tracking-wider mb-2 block">Mot de passe actuel</Label>
                    <Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-black/30 border-white/10"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-white/40 uppercase tracking-wider mb-2 block">Nouveau mot de passe</Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-black/30 border-white/10"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-white/40 uppercase tracking-wider mb-2 block">Confirmer le mot de passe</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-black/30 border-white/10"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button 
                    onClick={changePassword} 
                    disabled={passwordLoading}
                    className="w-full bg-blue-600 hover:bg-blue-500 rounded-xl py-3"
                    data-testid="change-password-btn"
                  >
                    {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                    Changer le mot de passe
                  </Button>
                  <button
                    onClick={() => navigate("/forgot-password")}
                    className="block w-full text-center text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              </div>

              <Button onClick={saveAccount} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-500 rounded-xl py-3" data-testid="save-account-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>

              {/* Danger Zone */}
              <div className="bg-red-950/20 border border-red-500/20 rounded-2xl p-5 sm:p-6">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2 text-red-400"><AlertTriangle className="w-5 h-5" />Zone de danger</h2>
                <p className="text-white/40 text-sm mb-2">Ces actions concernent directement votre compte.</p>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4 border-b border-white/5">
                  <div>
                    <p className="text-sm font-medium">Désactiver mon compte</p>
                    <p className="text-xs text-white/30">Temporaire — reconnectez-vous pour le réactiver.</p>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => { setDangerAction("deactivate"); setDangerPassword(""); }}
                    className="self-start sm:self-auto shrink-0 border-orange-500/30 text-orange-400 hover:bg-orange-500/10 text-xs"
                    data-testid="deactivate-account-btn"
                  >
                    Désactiver
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4">
                  <div>
                    <p className="text-sm font-medium">Supprimer définitivement mon compte</p>
                    <p className="text-xs text-white/30">Action irréversible — votre profil sera entièrement effacé.</p>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => { setDangerAction("delete"); setDangerPassword(""); setDeleteConfirmWord(""); }}
                    className="self-start sm:self-auto shrink-0 border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                    data-testid="delete-account-btn"
                  >
                    Supprimer
                  </Button>
                </div>

                {dangerAction && (
                  <div className="mt-2 p-4 rounded-xl bg-black/30 border border-red-500/20 space-y-3">
                    <p className="text-sm text-white/70">
                      {dangerAction === "deactivate"
                        ? "Confirmez avec votre mot de passe pour désactiver votre compte."
                        : "Cette action est irréversible. Confirmez avec votre mot de passe et tapez SUPPRIMER pour continuer."}
                    </p>
                    <Input
                      type="password"
                      value={dangerPassword}
                      onChange={(e) => setDangerPassword(e.target.value)}
                      placeholder="Mot de passe actuel"
                      className="bg-black/30 border-white/10"
                      data-testid="danger-password-input"
                    />
                    {dangerAction === "delete" && (
                      <Input
                        value={deleteConfirmWord}
                        onChange={(e) => setDeleteConfirmWord(e.target.value)}
                        placeholder='Tapez "SUPPRIMER"'
                        className="bg-black/30 border-white/10"
                        data-testid="delete-confirm-input"
                      />
                    )}
                    <div className="flex gap-2">
                      <Button variant="ghost" onClick={() => setDangerAction(null)} className="flex-1 text-white/50">Annuler</Button>
                      <Button
                        onClick={dangerAction === "deactivate" ? handleDeactivate : handleDeleteAccount}
                        disabled={dangerLoading || !dangerPassword || (dangerAction === "delete" && deleteConfirmWord !== "SUPPRIMER")}
                        className={`flex-1 ${dangerAction === "deactivate" ? "bg-orange-600 hover:bg-orange-500" : "bg-red-600 hover:bg-red-500"}`}
                        data-testid="danger-confirm-btn"
                      >
                        {dangerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : dangerAction === "deactivate" ? "Désactiver" : "Supprimer définitivement"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : tab === "security" ? (
            <div className="space-y-6">
              {/* 2FA Section */}
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-blue-400" />Authentification à deux facteurs</h2>

                {/* Email 2FA */}
                <div className="flex items-center justify-between gap-3 py-4 border-b border-white/5">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="w-5 h-5 text-white/40 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm">Par email</p>
                      <p className="text-xs text-white/30 truncate">{profile?.email}</p>
                    </div>
                  </div>
                  {twofa.email ? (
                    <Button size="sm" variant="ghost" onClick={() => disable2FA("email")} className="text-red-400 text-xs shrink-0">Désactiver</Button>
                  ) : (
                    <Button size="sm" onClick={() => setup2FA("email")} className="bg-blue-600 text-xs shrink-0" data-testid="enable-email-2fa">Activer</Button>
                  )}
                </div>

                {/* SMS 2FA */}
                <div className="flex items-center justify-between gap-3 py-4 border-b border-white/5">
                  <div className="flex items-center gap-3 min-w-0">
                    <Smartphone className="w-5 h-5 text-white/40 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm">Par SMS</p>
                      <p className="text-xs text-white/30">{phone || "Ajoutez un numéro d'abord"}</p>
                    </div>
                  </div>
                  {twofa.sms ? (
                    <Button size="sm" variant="ghost" onClick={() => disable2FA("sms")} className="text-red-400 text-xs shrink-0">Désactiver</Button>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-white/40 shrink-0" data-testid="sms-2fa-soon">Bientôt</span>
                  )}
                </div>

                {/* TOTP 2FA */}
                <div className="flex items-center justify-between gap-3 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Key className="w-5 h-5 text-white/40 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm">Google Authenticator</p>
                      <p className="text-xs text-white/30">Application d'authentification</p>
                    </div>
                  </div>
                  {twofa.totp ? (
                    <Button size="sm" variant="ghost" onClick={() => disable2FA("totp")} className="text-red-400 text-xs shrink-0">Désactiver</Button>
                  ) : (
                    <Button size="sm" onClick={() => setup2FA("totp")} className="bg-blue-600 text-xs shrink-0" data-testid="enable-totp-2fa">Activer</Button>
                  )}
                </div>
              </div>

              {/* Verify 2FA modal */}
              {verifyMethod && (
                <div className="bg-[#12121a] border border-blue-500/20 rounded-2xl p-5 sm:p-6">
                  <h3 className="text-sm font-semibold mb-4">
                    {verifyMethod === "totp" ? "Scanne le QR code avec ton application (Google Authenticator, Authy…), puis entre le code" : "Entre le code reçu par e-mail (pense à regarder les spams)"}
                  </h3>

                  {verifyMethod === "totp" && totpSecret && (
                    <div className="mb-4 space-y-3">
                      {totpQr && (
                        <div className="bg-black/30 p-4 rounded-lg text-center">
                          <img src={totpQr} alt="QR Code" data-testid="totp-qr" className="mx-auto rounded-lg w-full max-w-[200px] h-auto bg-white" />
                        </div>
                      )}
                      <div className="bg-black/30 p-3 rounded-lg">
                        <p className="text-xs text-white/30 mb-1">Ou entrez ce code manuellement :</p>
                        <p className="font-mono text-sm text-cyan-400 select-all break-all">{totpSecret}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Input
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      placeholder="Code à 6 chiffres"
                      className="bg-black/30 border-white/10 font-mono tracking-widest text-center"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      data-testid="2fa-code-input"
                    />
                    <Button onClick={verify2FA} className="bg-green-600 hover:bg-green-500" data-testid="verify-2fa-btn">
                      <Check className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    {verifyMethod === "email" && (
                      <button onClick={() => setup2FA("email")} className="text-blue-400 text-xs hover:text-blue-300" data-testid="resend-2fa-email">Renvoyer le code</button>
                    )}
                    <button onClick={() => { setVerifyMethod(null); setTotpSecret(null); setTotpQr(null); }} className="text-white/30 text-xs hover:text-white/50">Annuler</button>
                  </div>
                </div>
              )}

              {/* Appareils connectés */}
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Monitor className="w-5 h-5 text-blue-400" />Appareils connectés</h2>
                <p className="text-white/40 text-sm mb-4">
                  Les appareils actuellement connectés à votre compte. Déconnectez ceux que vous ne reconnaissez pas.
                </p>

                {sessionsLoading ? (
                  <div className="py-6 text-center text-white/30 text-sm">Chargement...</div>
                ) : sessions.length === 0 ? (
                  <div className="py-6 text-center text-white/30 text-sm">Aucun appareil connecté</div>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((s) => (
                      <div key={s.session_id} data-testid={`session-row-${s.session_id}`} className="flex items-center justify-between gap-3 py-3 border-b border-white/5 last:border-b-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <Monitor className="w-5 h-5 text-white/40 shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm truncate">{s.device_label}</p>
                              {s.is_current && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 shrink-0">Cet appareil</span>
                              )}
                            </div>
                            <p className="text-xs text-white/30 flex items-center gap-1 flex-wrap">
                              <MapPin className="w-3 h-3 shrink-0" />
                              {s.city ? `${s.city}${s.country ? `, ${s.country}` : ""}` : "Localisation inconnue"}
                              {s.ip && <span className="text-white/20">· {s.ip}</span>}
                            </p>
                            <p className="text-xs text-white/20">Dernière activité : {formatLastSeen(s.last_seen_at)}</p>
                          </div>
                        </div>
                        {!s.is_current && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => revokeSession(s.session_id)}
                            disabled={revokingId === s.session_id}
                            data-testid={`revoke-session-${s.session_id}`}
                            className="text-red-400 text-xs shrink-0 flex items-center gap-1"
                          >
                            <LogOut className="w-3.5 h-3.5" />
                            Déconnecter
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          ) : tab === "referral" ? (
            <div className="space-y-6">
              {referral ? (
                <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                  <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Gift className="w-5 h-5 text-blue-400" />Parrainage</h2>
                  <p className="text-white/40 text-sm mb-4">Invitez des amis : dès leur premier achat, vous recevez -20% sur le vôtre.</p>

                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/70 truncate">
                      {referral.referral_link}
                    </div>
                    <Button onClick={copyReferralLink} className="bg-blue-600 hover:bg-blue-500 shrink-0">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                    <div className="flex items-center gap-2 text-white/60">
                      <Users className="w-4 h-4 text-white/30" />
                      {referral.total_referred} filleul{referral.total_referred > 1 ? "s" : ""}
                    </div>
                    <div className="flex items-center gap-2 text-white/60">
                      <Gift className="w-4 h-4 text-white/30" />
                      {referral.total_rewards} récompense{referral.total_rewards > 1 ? "s" : ""} reçue{referral.total_rewards > 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-12 text-center text-white/30 text-sm">Chargement...</div>
              )}
            </div>
          ) : tab === "preferences" ? (
            /* Preferences tab */
            <div className="space-y-6">
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Languages className="w-5 h-5 text-blue-400" />{t('settings_language_title')}</h2>
                <p className="text-white/40 text-sm mb-4">{t('settings_language_desc')}</p>
                <div className="flex gap-2 bg-black/30 rounded-xl p-1 max-w-xs">
                  <button
                    onClick={() => setLang("fr")}
                    data-testid="lang-fr-btn"
                    className={`flex-1 py-2 text-sm rounded-lg transition-colors ${lang === "fr" ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}
                  >
                    {t('lang_fr')}
                  </button>
                  <button
                    onClick={() => setLang("en")}
                    data-testid="lang-en-btn"
                    className={`flex-1 py-2 text-sm rounded-lg transition-colors ${lang === "en" ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}
                  >
                    {t('lang_en')}
                  </button>
                </div>
              </div>

              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Clock className="w-5 h-5 text-blue-400" />Rester connecté</h2>
                <p className="text-white/40 text-sm mb-4">
                  Vous serez déconnecté automatiquement après cette durée d'inactivité (aucune visite sur le site).
                </p>
                <div className="flex gap-2 bg-black/30 rounded-xl p-1 max-w-md flex-wrap">
                  {[
                    { days: 7, label: "7 jours" },
                    { days: 30, label: "1 mois" },
                    { days: 60, label: "2 mois" },
                    { days: 90, label: "3 mois" },
                  ].map((opt) => (
                    <button
                      key={opt.days}
                      onClick={() => saveSessionDuration(opt.days)}
                      disabled={sessionDurationSaving}
                      data-testid={`session-duration-${opt.days}`}
                      className={`flex-1 py-2 px-3 text-sm rounded-lg transition-colors disabled:opacity-50 ${sessionDuration === opt.days ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : tab === "billing" ? (
            <SettingsBilling />
          ) : (
            /* Support tab */
            <div className="space-y-6" data-testid="settings-support">
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Headphones className="w-5 h-5 text-cyan-400" />{t('settings_support_title')}</h2>
                <p className="text-white/40 text-sm mb-5">{t('settings_support_subtitle')}</p>
                <div className="space-y-2.5">
                  <button
                    onClick={() => navigate('/tutorial')}
                    data-testid="settings-support-tutorial"
                    className="w-full flex items-center gap-3 p-3.5 bg-black/20 border border-white/5 rounded-xl text-left hover:bg-white/[0.04] hover:border-white/10 transition-colors"
                  >
                    <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-amber-500/15 text-amber-400"><BookOpen className="w-5 h-5" /></span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium">{t('settings_support_tutorial')}</span>
                      <span className="block text-xs text-white/35 truncate">{t('settings_support_tutorial_desc')}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-white/25 shrink-0" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}
